import {
  BehaviorSubject,
  Observable,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import {
  FormsService,
  minArrayLength,
} from 'src/app/core/services/forms.service';
import {
  NomAlertDeviationDirection,
  NomAlertTriggerType,
  NomExportTransportTypes,
  NomExternalShareOption,
  NomObjectClass,
  NomObjectTypes,
  NomOutputFormat,
  PopupType,
} from '../../constants';
import {
  NomPushServiceType,
  OutputSettingsType,
} from '../../models/core.models';
import { Store, select } from '@ngrx/store';
import { createAlert, editAlert } from '../../actions/feed.actions';
import {
  selectCurrentObject,
  selectObjectsByIds,
  selectOutputSettings,
  selectSelectedPrincipalsByIds,
  selectUserHasMattermostAccess,
} from '../../reducers';

import { Alert } from '../../models/alert.models';
import { AppState } from 'src/app/reducers';
import { FormProperty } from 'src/app/core/models/form.models';
import { GeneralDynamicFormComponent } from '../general-dynamic-form/general-dynamic-form.component';
import { LoggerService } from '@services/logger.service';
import { NomenclatureService } from '../../services/nomenclature.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { TranslateService } from '@ngx-translate/core';
import { fetchUserObjectsByIds } from '../../actions/object.actions';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { loadOutputSettings } from '../../actions/output-settings.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CoreService } from '../../services/core.service';

@Component({
  selector: 'app-alert-editor',
  templateUrl: './alert-editor.component.html',
  styleUrls: ['./alert-editor.component.scss'],
  standalone: false,
})
export class AlertEditorComponent
  extends GeneralDynamicFormComponent
  implements OnChanges
{
  @Input() alert: Alert | null;
  @Input() feedId: string;

  editorFormEntityName: string = 'alert';
  // ! TODO: both stream maybe need to be refactored!
  private dimensionIdsSubject = new BehaviorSubject<any[]>([]);
  private principalIdsSubject = new BehaviorSubject<any[]>([]);

  protected nomeclatureService = inject(NomenclatureService);
  protected editService = inject(ObjectEditorService);
  private coreService = inject(CoreService);

  constructor(
    protected store: Store<AppState>,
    protected formService: FormsService,
    private translateService: TranslateService,
    protected logger: LoggerService,
  ) {
    super(formService, store, logger);
  }

  getOutputSettingsOptions(): Observable<any[]> {
    return this.store.pipe(
      select(selectOutputSettings),
      map((outputSettings) =>
        outputSettings.map((os) => ({
          value: os.id,
          name: os.title,
        })),
      ),
    );
  }

  ngOnInit() {
    super.ngOnInit();

    if (this.alert) {
      this.populateAlert();
    } else {
      this.formGroup.patchValue({
        'trigger_type.id': NomAlertTriggerType.NEW_POSTS,
        'deviation_direction.id': NomAlertDeviationDirection.Up,
        deviation_period: 3600,
        notification_posts_count: 1,
        deviation_posts_count: 1,
        push_notifications: false,
        email_notifications: false,
        notify_users: false,
      });
    }

    this.store
      .select(selectUserHasMattermostAccess)
      .pipe(
        // only care about the first `false` value
        filter((has) => has === false),
        take(1),
        tap(() => this.hideMattermostOption()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectCurrentObject)
      .pipe(
        tap((object) => {
          if (object) {
            this.store.dispatch(
              loadOutputSettings({
                objectId: object?.object_id ?? null,
                objectClassId: object?.object_class_id ?? null,
              }),
            );
            const objectKey = OutputSettingsType.DOWNLOAD_SETTINGS;

            if (object[objectKey]) {
              const mmOutputSettingsId =
                this.alert?.notification_service_settings?.[
                  NomPushServiceType.MATTERMOST
                ]?.['output_settings_id'];

              const objectOutputSettingsId =
                object?.[objectKey]?.['output_settings_id'];

              const exportSettings = {
                output_settings_id:
                  mmOutputSettingsId ?? objectOutputSettingsId ?? null,
              };

              this.formService.populateFormValues(
                this.formGroup,
                exportSettings,
              );

              // Populate form
              this.formService.populateFormValues(
                this.formGroup,
                exportSettings,
              );
            }
          } else if (this.alert) {
            if (this.alert) {
              this.store.dispatch(
                loadOutputSettings({
                  objectId: this.alert.context_object.id ?? null,
                  objectClassId: this.alert.context_object.class_id ?? null,
                }),
              );

              const mmOutputSettingsId =
                this.alert?.notification_service_settings?.[
                  NomPushServiceType.MATTERMOST
                ]?.['output_settings_id'];

              const exportSettings = {
                output_settings_id: mmOutputSettingsId,
              };

              this.formService.populateFormValues(
                this.formGroup,
                exportSettings,
              );
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.formGroup
      .get('mattermost_server_id')
      ?.valueChanges.pipe(
        tap(() => {
          //this.logger.debug('Mattermost server changed, clearing channels');
          (this.formGroup.get('channels') as FormArray)?.clear();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['alert'] &&
      changes['alert'].firstChange === false &&
      this.alert
    ) {
      this.populateAlert();
    }
  }

  // …

  hideMattermostOption() {
    const arr = this.formGroup.get('notification_push_service') as FormArray;
    if (!arr) {
      return;
    }

    const mattermostValue = NomPushServiceType.MATTERMOST;
    // find the index of the MATTERMOST entry in the current value array
    const idx = arr.value.indexOf(mattermostValue);

    if (idx > -1) {
      // remove the FormControl at that index
      arr.removeAt(idx);
    }

    // update your UI config as before
    const property = this.formProperties.find(
      (p) => p.propertyName === 'notification_push_service',
    );
    if (property) {
      property.additionalConfig['values'] = property.additionalConfig?.[
        'values'
      ].filter((item) => item.value !== mattermostValue);
    }
  }

  private populateAlert() {
    const alertData = {
      ...this.alert,
      notification_push_service: this.alert.notification_push_service
        // only keep items whose `value` is true
        .filter((item) => item.value === true)
        // then pluck out the `id`
        .map((item) => item.id),

      notify_users: this.alert.notify_principal_object_class_list?.length > 0,
    };

    if (alertData.notification_push_service) {
      const mattermostValue = NomPushServiceType.MATTERMOST;
      // find the index of the MATTERMOST entry in the current value array
      const idx = alertData.notification_push_service.indexOf(mattermostValue);
      if (idx > -1) {
        // has mattermost value, so we need to add the mattermost server id
        alertData['mattermost_server_id'] =
          this.alert.notification_service_settings?.[
            NomPushServiceType.MATTERMOST
          ]?.['mattermost_server_id'];
        alertData['external_share_option_id'] =
          this.alert.notification_service_settings?.[
            NomPushServiceType.MATTERMOST
          ]?.['external_share_option_id'];
        alertData['channels'] =
          this.alert.notification_service_settings?.[
            NomPushServiceType.MATTERMOST
          ]?.['channels'];
      }
    }

    this.formService.populateFormValues(this.formGroup, alertData);
    this.dimensionIdsSubject.next(this.alert?.dimension_ids_filter || []);
    this.principalIdsSubject.next(
      this.alert?.notify_principal_object_class_list || [],
    );

    // Fetch user objects to populate the core state and display selected alert filters
    this.dimensionIdsSubject
      .asObservable()
      .pipe(
        take(1),
        tap((dimensionIds) => {
          this.store.dispatch(
            fetchUserObjectsByIds({ objectIds: dimensionIds }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onSubmitSuccess(formValue: any): void {
    const alertData = {
      ...formValue,
      deviation_direction: formValue['deviation_direction.id'],
      notification_push_service: formValue.notification_push_service?.map(
        (item: any) => ({ id: item, value: true }),
      ),
    };

    if (!this.alert || !this.alert?.id) {
      this.store.dispatch(
        createAlert({
          feedId: this.feedId,
          alert: alertData,
        }),
      );
    } else {
      this.store.dispatch(
        editAlert({
          alertId: this.alert.id,
          alert: alertData,
        }),
      );
    }
  }

  getAlertDimensions(): Observable<any[]> {
    return this.dimensionIdsSubject.asObservable().pipe(
      switchMap((dimensionIds) => {
        this.logger.debug('dimension ids for alert' + dimensionIds);
        if (!dimensionIds || dimensionIds.length === 0) {
          return of([]); // Return an empty array if no dimensions are set
        }

        return this.store.select(selectObjectsByIds(dimensionIds));
      }),
    );
  }

  getAlertPrincipalObjects(): Observable<any[]> {
    return this.principalIdsSubject.asObservable().pipe(
      switchMap((principalObjectClassList) => {
        const principalIds = principalObjectClassList.map(
          (item) => item.object_id,
        );

        if (!principalIds || principalIds.length === 0) {
          return of([]); // Return an empty array if no principal objects are set
        }
        return this.coreService
          .getPrincipalsByIds(principalObjectClassList)
          .pipe(map((response) => response.items));
      }),
    );
  }

  /**
   * Edits the output settings for a given outputSettingsId.
   * @param outputSettingsId - The ID of the output settings to edit.
   */

  editOutputSettings(outputSettingsId: number): void {
    this.store
      .select(selectCurrentObject)
      .pipe(
        // only proceed if we actually have a currentObject
        filter((currentObject) => !!currentObject),
        // take the first value and auto-unsubscribe
        take(1),
        // side-effect: call the edit service
        tap((currentObject) =>
          this.editService.editOutputSettings(
            outputSettingsId,
            currentObject.object_id,
            currentObject.object_class_id,
          ),
        ),
        // also ensure teardown if the component is destroyed before emission
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private isTriggerType(type: NomAlertTriggerType): boolean {
    return this.formGroup?.get(['trigger_type.id'])?.value === type;
  }

  containMattermost(formGroup: FormGroup): boolean {
    if (!formGroup) {
      return false;
    }

    // Check if the form group is defined
    const control = formGroup?.get(['notification_push_service']);
    const value = control?.value;

    if (Array.isArray(value)) {
      // when it's an array, check if MATTERMOST is one of the entries
      const result = value.some(
        (item) => item == NomPushServiceType.MATTERMOST,
      );

      return result;
    }

    // otherwise do a normal equality check
    return value == NomPushServiceType.MATTERMOST;
  }

  public formProperties: FormProperty[] = [
    {
      propertyName: 'title',
      propertyLabel: this.translateService.instant('alert-editor.label.title'),
      controlType: 'text',
      validators: [Validators.required],
      additionalConfig: {
        placeholder: this.translateService.instant(
          'alert-editor.input-title.placeholder',
        ),
      },
    },

    {
      propertyName: 'use_alert_title_as_subject',
      propertyLabel: this.translateService.instant(
        'alert-editor.label.use-alert-title-as-subject',
      ),
      controlType: 'toggle',
      defaultValue: true,
      formChanger: true,
      additionalConfig: {
        size: 'small',
      },
    },

    {
      propertyName: 'description',
      propertyLabel: this.translateService.instant(
        'alert-editor.label.description',
      ),
      controlType: 'textarea',
      additionalConfig: {
        placeholder: this.translateService.instant(
          'alert-editor.input-description.placeholder',
        ),
        rows: 2,
      },
    },
    {
      propertyName: 'trigger_type.id',
      validators: [Validators.required],
      propertyLabel: this.translateService.instant(
        'alert-editor.label.alert-condition',
      ),
      controlType: 'select',
      valueOptions: this.formService.alertTriggerTypes,
      formChanger: true, // changes of this property modify form
    },

    {
      propertyName: 'notification_posts_count',
      validateBy: (formGroup) => {
        if (this.isTriggerType(NomAlertTriggerType.NEW_POSTS)) {
          // Apply Validators.required when the control is displayed
          return [Validators.required];
        }
        return [];
      },
      propertyLabel: this.translateService.instant(
        'alert-editor.label.notifications-posts-count',
      ),
      controlType: 'input-number',
      shouldDisplay: (formGroup) => {
        return this.isTriggerType(NomAlertTriggerType.NEW_POSTS);
      },
    },

    {
      propertyName: 'deviation_posts_count',
      validateBy: (formGroup) => {
        if (this.isTriggerType(NomAlertTriggerType.NEW_POSTS)) {
          // Apply Validators.required when the control is displayed
          return [Validators.required];
        }
        return [];
      },
      propertyLabel: this.translateService.instant(
        'alert-editor.label.deviation-posts-count',
      ),
      controlType: 'input-number',
      shouldDisplay: (formGroup) => {
        return this.isTriggerType(NomAlertTriggerType.NEW_POSTS);
      },
    },

    {
      propertyName: 'deviation_threshold_value',
      validateBy: (formGroup) => {
        if (this.isTriggerType(NomAlertTriggerType.PERCENT_CHANGE)) {
          // Apply Validators.required when the control is displayed
          return [Validators.required];
        }
        return [];
      },
      propertyLabel: this.translateService.instant(
        'alert-editor.label.change-amount',
      ),
      controlType: 'select',
      valueOptions: this.formService.alertDeviationOptions,
      shouldDisplay: (formGroup) => {
        return this.isTriggerType(NomAlertTriggerType.PERCENT_CHANGE);
      },
    },

    {
      propertyName: 'deviation_direction.id',
      validateBy: (formGroup) => {
        if (this.isTriggerType(NomAlertTriggerType.PERCENT_CHANGE)) {
          return [Validators.required];
        }
        return [];
      },
      propertyLabel: this.translateService.instant(
        'alert-editor.label.deviation-direction',
      ),
      controlType: 'select',
      valueOptions: this.formService.alertDeviationDirection,
      shouldDisplay: (formGroup) => {
        return this.isTriggerType(NomAlertTriggerType.PERCENT_CHANGE);
      },
    },

    {
      propertyName: 'deviation_period',
      validateBy: (formGroup) => {
        if (this.isTriggerType(NomAlertTriggerType.PERCENT_CHANGE)) {
          // Apply Validators.required when the control is displayed
          return [Validators.required];
        }
        return [];
      },
      propertyLabel: this.translateService.instant(
        'alert-editor.label.period-of-change',
      ),
      controlType: 'select',
      valueOptions: this.formService.alertDeviationPeriod,
      shouldDisplay: (formGroup) => {
        return this.isTriggerType(NomAlertTriggerType.PERCENT_CHANGE);
      },
    },

    {
      propertyName: 'notification_push_service',
      controlType: 'checkbox-array',
      propertyLabel: this.translateService.instant(
        'alert-editor.label.alert-delivery-methods',
      ),
      formChanger: true,
      additionalConfig: {
        values: [
          {
            value: NomPushServiceType.ONE_SIGNAL,
            name: this.translateService.instant(
              'alert-editor.label.mobile-push-notification',
            ),
            icon: ['fas', 'mobile-alt'],
          },

          {
            value: NomPushServiceType.EMAIL,
            name: this.translateService.instant(
              'alert-editor.label.email-notification',
            ),
            icon: ['fas', 'envelope'],
          },
          {
            value: NomPushServiceType.MATTERMOST,
            name: this.translateService.instant(
              'alert-editor.label.mattermost-notification',
            ),
            icon: ['fas', 'turn-up'], // custom mattermost icon
          },
        ],
      },
      validateBy: (formGroup) => {
        return [Validators.required, minArrayLength(1)];
      },
    },

    {
      propertyName: 'notify_users',
      propertyLabel: this.translateService.instant(
        'alert-editor.label.trigger-notification-for-list-of-users',
      ),
      controlType: 'toggle',
      formChanger: true,
      additionalConfig: {
        size: 'small',
      },
    },

    {
      propertyName: 'notify_principal_object_class_list',
      propertyLabel: this.translateService.instant(
        'alert-editor.label.list-of-users-to-be-notified',
      ),

      controlType: 'principal-picker',
      defaultValue: [],
      additionalConfig: {
        label: this.translateService.instant(
          'alert-editor.button.assign-users',
        ),
        leadingIcon: ['fas', 'user-plus'],
        variant: 'secondary',
        principals$: this.getAlertPrincipalObjects(),
        subject$: this.principalIdsSubject,
      },

      shouldDisplay: (formGroup) => {
        return formGroup?.get('notify_users')?.value;
      },
    },

    {
      propertyName: 'dimension_ids_filter',
      propertyLabel: this.translateService.instant(
        'alert-editor.label.assign-filters',
      ),

      controlType: 'dataobject-picker',
      defaultValue: [],
      additionalConfig: {
        objectClassIds: [NomObjectClass.FEED],
        objectTypeIds: [NomObjectTypes.SEARCH_DIMENSION],
        label: this.translateService.instant(
          'alert-editor.button.assign-filters',
        ),
        leadingIcon: ['fas', 'filter'],
        variant: 'secondary',
        popupType: PopupType.ATTACH_FILTERS,
        objects$: this.getAlertDimensions(),
        subject$: this.dimensionIdsSubject,
        singleSelectionActive: true,
        removeButton: true,
        removeAction: () => {
          this.dimensionIdsSubject.next([]);
          this.formGroup.patchValue({ ['dimension_ids_filter']: [] });
        },
      },
    },

    // mattermost items

    {
      propertyName: 'mattermost_server_id',
      propertyLabel: this.translateService.instant(
        'export-settings.label.mattermost-server',
      ),
      controlType: 'select',
      valueOptionsAsync: this.nomeclatureService.fetchMattermostServers().pipe(
        map((servers) =>
          this.formService.transformForSelect(
            servers,
            (server) => server.id,
            (server) => server.display_name,
            null,
          ),
        ),
        tap((options) => {
          const control = this.formGroup.get('mattermost_server_id');
          if (control && !control.value && options && options.length > 0) {
            control.setValue(options[0].value);
          }
        }),
      ),

      shouldDisplay: (formGroup) => {
        return this.containMattermost(formGroup);
      },

      validateBy: (formGroup) => {
        return this.containMattermost(formGroup) ? [Validators.required] : [];
      },
    },

    {
      propertyName: 'output_settings_id',
      controlType: 'select',
      propertyLabel: this.translateService.instant(
        'export-settings.label.select-output-settings',
      ),
      valueOptionsAsync: this.getOutputSettingsOptions(),
      shouldDisplay: (formGroup: FormGroup): boolean => {
        return this.containMattermost(formGroup);
      },

      validateBy: (formGroup) => {
        return this.containMattermost(formGroup) ? [Validators.required] : [];
      },

      additionalConfig: {
        buttons: [
          {
            leadingIcon: ['fas', 'gear'],
            variant: 'primary',
            visible: true,
            enabled: true,
            action: this.editOutputSettings.bind(this),
          },
        ],
      },
    },

    {
      propertyName: 'external_share_option_id',
      propertyLabel: this.translateService.instant(
        'export-settings.label.share-options',
      ),

      controlType: 'select',
      valueOptions: this.formService.externalShareOptions,
      shouldDisplay: (formGroup) => {
        return this.containMattermost(formGroup);
      },
      validateBy: (formGroup) => {
        return this.containMattermost(formGroup) ? [Validators.required] : [];
      },
      defaultValue: NomExternalShareOption.CONTENT_EXCERPT,
    },

    {
      propertyName: 'channels',
      propertyLabel: this.translateService.instant(
        'export-settings.label.mattermost-channels',
      ),
      controlType: 'checkbox-array',
      additionalConfig: {
        valuesAsync: (formGroup: FormGroup) => {
          // Cache the observable on the form group to prevent multiple identical subscriptions.
          if (!formGroup['channelsValueOptions$']) {
            const mattermostServerControl = formGroup.get(
              'mattermost_server_id',
            );
            formGroup['channelsValueOptions$'] =
              mattermostServerControl.valueChanges.pipe(
                // Start with the current value so that the chain fires immediately.
                startWith(mattermostServerControl.value),
                // Filter out null/undefined values if needed.
                filter((serverId) => serverId != null),
                // Prevent duplicate successive values.
                distinctUntilChanged(),
                // Cancel previous requests if a new serverId comes in.
                switchMap((serverId: number) =>
                  this.nomeclatureService.fetchMattermostChannels(serverId),
                ),
                // Transform the channels into select options.
                map((channels) =>
                  this.formService.transformForSelect(
                    channels,
                    (channel) => channel.channel_id,
                    (channel) => channel.name,
                    null,
                  ),
                ),
                // Share the latest value with all subscribers.
                shareReplay(1),
              );
          }
          // Return the cached observable.
          return formGroup['channelsValueOptions$'];
        },
      },
      shouldDisplay: (formGroup) => {
        return this.containMattermost(formGroup);
      },

      validateBy: (formGroup) => {
        return this.containMattermost(formGroup)
          ? [Validators.required, minArrayLength(1)]
          : [];
      },
    },
  ];
}
